/**
 * ChatWorkspace.tsx — the unified three-column chat workspace (Design Spec §01–§03).
 *
 * Relocated from the chatter app's pages/ChatterWorkspace.tsx wrapper into @crm/chat (Phase 12-02,
 * D-01/D-01a) so the admin all-chats-access surface (Phase 12-03) can reuse the identical,
 * battle-tested component instead of forking it (RESEARCH.md Anti-Patterns).
 *
 * Collapses the old two-screen split (flat inbox list ↔ separate conversation route)
 * into ONE screen: queue (left) · active conversation (centre) · fan context (right),
 * under a persistent header + per-creator tab strip. Selecting a fan never navigates
 * away — it updates the centre + right columns in place (§02 "never lose context").
 *
 * URL (deep-linkable, back-button friendly):
 *   /inbox                         → redirect to first creator
 *   /inbox/:creatorId              → workspace, no thread open
 *   /inbox/:creatorId/c/:convId    → workspace with that thread open
 *
 * Data:
 *   - GET /creators/mine drives the tab strip + active creator (assignment-scoped, T-05-20).
 *   - The active creator's accounts are fetched + merged into one conversation list
 *     (queryKey ['inbox', creatorId]); inbox:delta invalidates it live (D-05-05).
 *   - health:update keeps the worst-of health pills current (D-18).
 *
 * Chatter-only extras (mass-messages Broadcasts modal, the Conversations|Notifications
 * toggle + Transactions pane, the tab tx-unread badge, and the FanPanel "Recent payments"
 * data source) are NOT wired directly — @crm/chat never imports transactionsApi/
 * broadcastsApi (Phase 12-02 design note). They are OPTIONAL injected slot props, supplied
 * by the chatter app's pages/ChatterWorkspace.tsx wrapper (a thin wrapper) in chatter mode and
 * omitted in the admin read-only surface (Phase 12-03).
 */

import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
  useMutation,
  type InfiniteData,
} from '@tanstack/react-query';
import {
  creatorsApi,
  inboxApi,
  conversationsApi,
  type CreatorAccountSummary,
  type CreatorRollupDto,
  type InboxFilterMode,
} from './lib/api-client.js';
import { useToast } from '@crm/ui';
import type {
  InboxConversationDto,
  InboxListDto,
  HealthUpdate,
  InboxDelta,
  InboxDeltaItem,
} from '@crm/types';
import { useWsClient } from './lib/WsProvider.js';
import { CreatorTabStrip } from './components/CreatorTabStrip.js';
import { ConversationListPane } from './components/ConversationListPane.js';
import { ConversationPane } from './components/ConversationPane.js';
import { FanPanel, type FetchRecentPayments } from './components/FanPanel.js';
import type { HealthMap } from './lib/health.js';
import { flattenInboxPages, nextInboxCursor } from './lib/inbox-pages.js';

export interface ChatWorkspaceProps {
  /**
   * Chatter-only: mass-messages broadcast modal component. Omit to hide the
   * "Mass messages" button + feature entirely (e.g. admin read-only mode, Phase 12-03).
   */
  BroadcastsModal?: React.ComponentType<{
    open: boolean;
    onClose: () => void;
    creatorName: string;
    accounts: CreatorAccountSummary[];
  }>;
  /**
   * Chatter-only: the Notifications/Transactions pane component. Omit to hide the
   * Conversations|Notifications toggle (the pane never renders without it).
   */
  TransactionsPane?: React.ComponentType<{
    creatorId: string;
    creatorName: string;
    basePath?: string;
    allCreators?: boolean;
  }>;
  /** Batch tx-unread counts per creatorId (D-07-15 tab badge). Omit when TransactionsPane is omitted. */
  txUnreadMap?: Record<string, number>;
  /**
   * Fired when the Notifications view opens for a creator (mark-seen dedupe lives here in
   * ChatWorkspace; only the actual API call is injected). Omit when TransactionsPane is omitted.
   */
  onTransactionsViewOpen?: (creatorId: string) => void;
  /**
   * Fired when the MERGED Notifications view opens on the "All" tab (D-13-03). This slot's
   * PRESENCE (not just truthiness — its being supplied at all) is what enables the merged
   * Notifications feed on the All tab: the two `!isAllTab` render guards relax to also allow
   * the All tab only when this prop is supplied. Admin's Chats.tsx omits it, so admin's All
   * tab stays byte-identical (T-13-09); the chatter wrapper supplies it, wired to
   * transactionsApi.markSeenAll(). Omit when TransactionsPane is omitted.
   */
  onAllTransactionsViewOpen?: () => void;
  /** Fetch a fan's recent per-tx payments for FanPanel's "Recent payments" section. */
  fetchRecentPayments?: FetchRecentPayments;
  /**
   * All-accounts mode (Phase 12-03, D-02/D-04). When set:
   *   - the default landing tab is "All" (a merged cross-account list from GET /inbox, the
   *     server admin branch) instead of auto-redirecting to the first creator;
   *   - the on-tab-open live-refresh fan-out (`inboxApi.refresh`) is skipped entirely
   *     (Pitfall 2 — never multiply live egress across tens of creators);
   *   - the passive-on-open side-effects (collision claim + auto-mark-read) are suppressed —
   *     an admin scanning many accounts must not stamp "being viewed" or clear a chatter's
   *     unread signal merely by opening a thread (T-12-11). Explicit clicks still act.
   * Chatter mode (omitted/false) is byte-identical to pre-Phase-12 behavior.
   */
  allAccountsMode?: boolean;
  /**
   * Hide every WRITE/action control (composer, PPV/media, mute, block/unblock/report, delete,
   * mark-unread, fan notes/tags, mass-messages). Defaults to `!!allAccountsMode` — so an
   * all-accounts surface is read-only by default. The admin full-parity chat surface passes
   * `readOnly={false}` explicitly (owner 2026-07-20 — admins send + do everything chatters do),
   * while STILL keeping the passive-on-open side-effects suppressed via allAccountsMode above.
   * Chatter mode (omitted, allAccountsMode falsy) resolves to false — byte-identical to before.
   */
  readOnly?: boolean;
  /**
   * Route prefix this instance is mounted under (Phase 12-04). All internal `navigate()`
   * calls are built from this prefix instead of the hardcoded `/inbox` literal, so a host
   * app can mount the workspace at a different path (e.g. admin-app's `/chats`) without
   * its tab/conversation navigation pushing a URL the host router doesn't recognize.
   * Defaults to `/inbox` — chatter mode (which never sets this) is byte-identical.
   */
  basePath?: string;
  /**
   * Suppress the passive-on-open side-effects (collision claim + auto-mark-read) even when
   * host defaults would otherwise enable them. Defaults to `readOnly || !!allAccountsMode`
   * (the pre-Phase-13 admin formula) so admin is byte-identical when omitted. The chatter
   * all-chats surface (Phase 13, D-13-05) passes `false` explicitly — chatters are operators,
   * not oversight viewers, so opening a thread on the All tab must still claim + mark-read.
   */
  suppressPassiveOnOpen?: boolean;
  /**
   * Default landing is the merged "All" tab (skips the auto-redirect-to-first-creator
   * effect) rather than the first-creator tab. Defaults to `!!allAccountsMode` (the
   * pre-Phase-13 admin formula) so admin is byte-identical when omitted. The chatter
   * all-chats surface (Phase 13, D-13-02) passes `false` explicitly — a fresh chatter login
   * must still land on the first-creator tab; the All tab is opt-in via a click.
   */
  defaultToAllTab?: boolean;
  /**
   * Skip the on-open live-refresh fan-out (`inboxApi.refresh`) on EVERY tab, not just the
   * merged All tab. Defaults to `!!allAccountsMode` (the pre-Phase-13 admin formula) so admin
   * is byte-identical when omitted. The chatter all-chats surface (Phase 13, D-13-04) passes
   * `false` explicitly — a chatter opening a per-creator tab still needs the live pull; only
   * the All tab (gated separately via `isAllTab` in the effect) skips it.
   */
  disablePerCreatorRefresh?: boolean;
}

export function ChatWorkspace({
  BroadcastsModal,
  TransactionsPane,
  txUnreadMap,
  onTransactionsViewOpen,
  onAllTransactionsViewOpen,
  fetchRecentPayments,
  allAccountsMode,
  readOnly: readOnlyProp,
  basePath = '/inbox',
  suppressPassiveOnOpen: suppressPassiveOnOpenProp,
  defaultToAllTab: defaultToAllTabProp,
  disablePerCreatorRefresh: disablePerCreatorRefreshProp,
}: ChatWorkspaceProps) {
  // readOnly hides the WRITE controls. It defaults to !!allAccountsMode (an all-accounts
  // surface is read-only unless told otherwise), but the admin full-parity surface passes
  // readOnly={false} to enable send/PPV/mute/block/delete/broadcasts (owner 2026-07-20).
  // Hiding controls here is a UX default only (T-12-05); the server routes are unchanged.
  const readOnly = readOnlyProp ?? !!allAccountsMode;
  // Passive-on-open side-effects (collision claim + auto-mark-read) are suppressed for the
  // all-accounts OVERSIGHT surface and for any read-only surface — a supervising admin must
  // not stamp "being viewed" or wipe a chatter's unread signal merely by opening a thread
  // (T-12-11), even when they CAN send. Explicit clicks (send/mute/block/mark-unread) still act.
  // (Phase 13, T-13-01) Explicit prop, defaulted to the pre-Phase-13 admin formula so admin
  // stays byte-identical when omitted; the chatter all-chats surface passes false (D-13-05).
  const suppressPassiveOnOpen = suppressPassiveOnOpenProp ?? (readOnly || !!allAccountsMode);
  // (Phase 13, T-13-01) Default landing tab. Defaults to the pre-Phase-13 admin formula
  // (!!allAccountsMode); the chatter all-chats surface passes false (D-13-02) so a fresh
  // chatter login still lands on the first-creator tab.
  const defaultToAllTab = defaultToAllTabProp ?? !!allAccountsMode;
  // (Phase 13, T-13-01/T-13-02) Skip the on-open live-refresh fan-out on every tab. Defaults
  // to the pre-Phase-13 admin formula (!!allAccountsMode); the chatter all-chats surface
  // passes false (D-13-04) so per-creator tabs keep their live refresh — only the merged All
  // tab (gated separately via isAllTab in the effect below) skips it.
  const disablePerCreatorRefresh = disablePerCreatorRefreshProp ?? !!allAccountsMode;
  const { creatorId: creatorIdFromUrl, conversationId } = useParams<{
    creatorId?: string;
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const ws = useWsClient();
  const { addToast } = useToast();
  const [broadcastsOpen, setBroadcastsOpen] = React.useState(false);

  // View toggle: 'conversations' | 'transactions' — driven by URL ?view= param.
  // Using URL state so deep-links work + browser back/forward are reliable.
  // IMPORTANT: the ?view=transactions path does NOT shadow /inbox/:creatorId/c/:convId
  // because it lives on /inbox/:creatorId — no new route is added (Pitfall 7).
  const [searchParams] = useSearchParams();
  const activeView = (searchParams.get('view') ?? 'conversations') as 'conversations' | 'transactions';

  const [healthMap, setHealthMap] = React.useState<HealthMap>({});
  // Inbox list filter chips (owner 2026-07-11): 'all' | 'unread' | 'unanswered'.
  // 'unread'/'unanswered' are SERVER-side (?filter= in the WHERE clause) so they search
  // the whole account — unlike search/muted, which only see the loaded page. Part of the
  // react-query key so each mode caches independently.
  const [inboxFilter, setInboxFilter] = React.useState<InboxFilterMode>('all');
  // On-demand live refresh: true while we're pulling the active creator's inbox from
  // Maloum (owner decision 2026-06-28 — refresh on tab open/switch). The ref dedupes a
  // single activation so React StrictMode / creators-refetch don't double-fire.
  const [refreshing, setRefreshing] = React.useState(false);
  const lastRefreshKeyRef = React.useRef<string | null>(null);

  // ---- Creators (tab strip) ----
  const {
    data: creators,
    isLoading: creatorsLoading,
    isError: creatorsError,
    error: creatorsErrorObj,
  } = useQuery({
    queryKey: ['creators', 'mine'],
    queryFn: () => creatorsApi.mine(),
    staleTime: 60_000,
  });

  // Admin all-accounts mode (D-04): the default landing tab is "All" (no creatorId in the
  // URL) rather than auto-redirecting to the first creator. isAllTab === true means the
  // merged cross-account list (allInboxQuery, below) drives the centre list instead of the
  // per-creator fan-out. Chatter mode (allAccountsMode falsy) is byte-identical to before —
  // it always falls through to the first creator, matching the auto-redirect effect below.
  // The "All" tab is either the bare basePath (no segment) OR the 'all' sentinel segment
  // (used when a conversation is open on the All tab: /chats/all/c/:id — see
  // handleSelectConversation). Both keep isAllTab true so the merged list stays active and
  // activeCreatorId stays null (per-creator fan-out disabled). The existing
  // :creatorId/c/:conversationId route matches `all/c/:id`, so no new route is needed.
  const isAllTab = !!allAccountsMode && (!creatorIdFromUrl || creatorIdFromUrl === 'all');
  const activeCreatorId = allAccountsMode
    ? (creatorIdFromUrl && creatorIdFromUrl !== 'all' ? creatorIdFromUrl : null)
    : (creatorIdFromUrl ?? creators?.[0]?.creatorId ?? null);
  const activeCreator: CreatorRollupDto | undefined = creators?.find(
    (c) => c.creatorId === activeCreatorId,
  );

  // accountId → creatorId (for inbox:delta routing)
  const accountToCreator = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const creator of creators ?? []) {
      for (const acc of creator.accounts) map[acc.accountId] = creator.creatorId;
    }
    return map;
  }, [creators]);

  // Stable comma-joined account-id signature of the active creator (effect dep).
  const activeAccountKey = React.useMemo(
    () => (activeCreator?.accounts ?? []).map((a) => a.accountId).join(','),
    [activeCreator],
  );

  // ---- Live refresh on creator open/switch (owner decision 2026-06-28) ----
  // When the active creator changes, pull each of its accounts' inbox live from Maloum
  // (POST /inbox/:accountId/refresh), then invalidate the cached list so it re-reads the
  // updated snapshot. Refresh failures are non-fatal — the cached snapshot still serves.
  React.useEffect(() => {
    // Pitfall 1 (Phase 13, T-13-02, CRITICAL): this guard must distinguish "skip because
    // this IS the merged All tab" (isAllTab — role-agnostic, always true on that tab) from
    // "skip on every tab because this deployment never wants per-creator refresh"
    // (disablePerCreatorRefresh — admin, preserved by default via !!allAccountsMode). Using
    // `isAllTab` alone would restore per-creator refresh fan-out for admin; using the raw
    // `allAccountsMode` alone would suppress the chatter's per-creator-tab refresh too.
    if (isAllTab || disablePerCreatorRefresh) return;
    const ids = activeAccountKey ? activeAccountKey.split(',') : [];
    if (!activeCreatorId || ids.length === 0) return;
    const key = `${activeCreatorId}|${activeAccountKey}`;
    if (lastRefreshKeyRef.current === key) return; // already refreshed this activation
    lastRefreshKeyRef.current = key;

    setRefreshing(true);
    Promise.allSettled(ids.map((id) => inboxApi.refresh(id)))
      .then(() => queryClient.invalidateQueries({ queryKey: ['inbox', activeCreatorId] }))
      .finally(() => setRefreshing(false));
  }, [activeCreatorId, activeAccountKey, queryClient, isAllTab, disablePerCreatorRefresh]);

  // ---- Active creator's conversation list ----
  // Key includes the filter mode; prefix invalidations (['inbox', creatorId]) still hit
  // every mode. The optimistic row patches go through patchInboxRow, which uses setQueriesData
  // with the 2-element prefix key (['inbox', creatorId] / ['inbox','all']) so ALL filter-mode
  // caches heal together — matching the prefix-based invalidate/cancel in the same mutations.
  const serverFilter = inboxFilter === 'all' ? undefined : inboxFilter;
  const inboxQuery = useQuery({
    queryKey: ['inbox', activeCreatorId, inboxFilter],
    enabled: !!activeCreator && !isAllTab,
    staleTime: 30_000,
    // Chip switches keep the previous rows visible (no full-pane "Loading inbox…"
    // flash) — but ONLY across filter changes for the SAME creator; a creator switch
    // must never flash another creator's conversations.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === activeCreatorId ? prev : undefined,
    queryFn: async () => {
      const accounts = activeCreator?.accounts ?? [];
      const results = await Promise.all(
        accounts.map((acc) => inboxApi.forAccount(acc.accountId, undefined, 100, serverFilter)),
      );
      const all: InboxConversationDto[] = [];
      for (const page of results) all.push(...page.conversations);
      all.sort((a, b) => {
        if (!a.lastMessageAt && !b.lastMessageAt) return 0;
        if (!a.lastMessageAt) return 1;
        if (!b.lastMessageAt) return -1;
        return b.lastMessageAt.localeCompare(a.lastMessageAt);
      });
      return all;
    },
  });

  // Admin "All" tab page size — one server page per fetchNextPage. The list pages through
  // the whole fleet via cursor pagination (allInboxQuery below), so an agency with tens of
  // accounts + a creator carrying 25k+ conversations is no longer capped at the 100 most
  // recently active rows. A "Load more" affordance pulls the next page on demand.
  const ALL_TAB_PAGE_SIZE = 100;

  // ---- Admin "All" tab: the server-merged cross-account inbox (D-04, Open Question 1) ----
  // `GET /inbox` (no accountId) already returns the admin-branch merged+keyset-paginated
  // list server-side (InboxService.getInbox, role==='admin') — a better fit than fanning the
  // per-creator pattern out across tens of creators. Enabled only while the "All" tab is
  // active; the per-creator inboxQuery above stays disabled in that state (isAllTab), so
  // only one of the two ever fetches.
  //
  // Cursor: the server keysets by conversation.last_message_at
  // (WHERE last_message_at < cursor, ORDER BY last_message_at DESC). It reports `hasMore`
  // but does not (yet) emit `nextCursor`, so getNextPageParam derives the cursor from the
  // last row's lastMessageAt — preferring page.nextCursor should a future backend send it.
  // undefined ⇒ no further pages (pagination is exhaustive). Pages are flattened + de-duped
  // by id in `conversations` below (keyset boundaries + live refetches can repeat a row).
  const allInboxQuery = useInfiniteQuery({
    queryKey: ['inbox', 'all', inboxFilter],
    enabled: isAllTab,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: InboxListDto) => nextInboxCursor(lastPage),
    queryFn: ({ pageParam }) => inboxApi.list(pageParam, ALL_TAB_PAGE_SIZE, serverFilter),
  });

  // Single accessor for the shared status flags (isLoading/isError/isFetching/…), regardless
  // of which list is active. NOTE: never read `.data` off this union — the two queries have
  // different data shapes (bare array vs InfiniteData); `conversations` below normalizes it.
  const activeInboxQuery = isAllTab ? allInboxQuery : inboxQuery;
  // Flatten the admin "All" tab's paged infinite-query result into one array, de-duping by
  // conversation id: keyset boundaries + live inbox:delta refetches can surface the same
  // conversation on two pages (a row whose lastMessageAt updates jumps to page 1). The
  // per-creator path returns its bare array unchanged, so chatter mode is byte-identical.
  const conversations = React.useMemo<InboxConversationDto[]>(() => {
    if (isAllTab) return flattenInboxPages(allInboxQuery.data?.pages ?? []);
    return inboxQuery.data ?? [];
  }, [isAllTab, allInboxQuery.data, inboxQuery.data]);
  // The open thread must survive a filter/list change that drops its row (e.g. replying
  // under "Unanswered" removes the row on refetch — the thread pane must NOT blank out).
  // Resolution order:
  //   1. the active (filtered) list;
  //   2. any OTHER cached inbox list for this creator (usually 'all') — fresher than a
  //      frozen snapshot, and mutations (mute, markRead) patch these caches via
  //      setQueriesData so the open pane keeps healing (review finding: a frozen
  //      snapshot froze the header Mute toggle);
  //   3. the last matched row (ref) while the URL still points at it.
  const lastSelectedConvRef = React.useRef<InboxConversationDto | null>(null);
  const foundConv = conversationId
    ? conversations.find((c) => c.id === conversationId) ?? null
    : null;
  const cachedConv = React.useMemo(() => {
    if (!conversationId || foundConv) return null;
    // isAllTab scans the ['inbox','all',*] cache (the merged admin list — an InfiniteData of
    // pages); otherwise the per-creator ['inbox', activeCreatorId, *] cache (a bare array).
    if (isAllTab) {
      for (const [, data] of queryClient.getQueriesData<InfiniteData<InboxListDto>>({
        queryKey: ['inbox', 'all'],
      })) {
        for (const pg of data?.pages ?? []) {
          const hit = pg.conversations.find((c) => c.id === conversationId);
          if (hit) return hit;
        }
      }
      return null;
    }
    for (const [, data] of queryClient.getQueriesData<InboxConversationDto[]>({
      queryKey: ['inbox', activeCreatorId],
    })) {
      const hit = data?.find((c) => c.id === conversationId);
      if (hit) return hit;
    }
    return null;
    // `conversations` is a dep so cache patches on the active key re-run this scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, foundConv, queryClient, activeCreatorId, isAllTab, conversations]);
  React.useEffect(() => {
    const best = foundConv ?? cachedConv;
    if (best) lastSelectedConvRef.current = best;
  }, [foundConv, cachedConv]);
  const selectedConv =
    foundConv ??
    cachedConv ??
    (conversationId && lastSelectedConvRef.current?.id === conversationId
      ? lastSelectedConvRef.current
      : null);

  // ---- Notifications view: mark-seen dedupe (owner decision 2026-07-02) ----
  // When the Transactions view opens for the active creator, notify the caller so it can
  // mark-seen + clear the badge (transactionsApi stays app-local (not shared) — Phase 12-02).
  // Other creators' badges remain accurate. The activation dedupe lives here so the
  // injected callback is only ever fired once per creator|view activation.
  //
  // Phase 13 (D-13-03): the single-creator path above literally cannot fire on the "All" tab
  // (activeCreatorId is null there), so the merged view gets its OWN activation-deduped path —
  // 'all|transactions' — firing onAllTransactionsViewOpen instead of onTransactionsViewOpen.
  const lastMarkSeenRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (activeView !== 'transactions') return;
    if (isAllTab) {
      const key = 'all|transactions';
      if (lastMarkSeenRef.current === key) return; // already marked this activation
      lastMarkSeenRef.current = key;
      onAllTransactionsViewOpen?.();
      return;
    }
    if (!activeCreatorId) return;
    const key = `${activeCreatorId}|transactions`;
    if (lastMarkSeenRef.current === key) return; // already marked this activation
    lastMarkSeenRef.current = key;
    onTransactionsViewOpen?.(activeCreatorId);
  }, [activeView, activeCreatorId, isAllTab, onTransactionsViewOpen, onAllTransactionsViewOpen]);

  // ---- WS subscriptions ----
  React.useEffect(() => {
    const unsub = ws.onHealthUpdate((evt: HealthUpdate) => {
      setHealthMap((prev) => ({ ...prev, [evt.accountId]: evt.state }));
    });
    return unsub;
  }, [ws]);

  React.useEffect(() => {
    const unsub = ws.onInboxDelta((evt: InboxDelta | InboxDeltaItem[]) => {
      // Tolerate the legacy bare-array payload (pre-envelope API during deploy skew).
      // The old handler dereferenced evt.items on an array and threw on every event,
      // so live deltas never updated the UI at all — never let a shape mismatch take
      // this path down again.
      const items = Array.isArray(evt) ? evt : (evt.items ?? []);
      const accountId = Array.isArray(evt) ? null : evt.accountId;
      const creatorIdForAccount = accountId ? accountToCreator[accountId] : null;
      if (creatorIdForAccount) {
        queryClient.invalidateQueries({ queryKey: ['inbox', creatorIdForAccount] });
        queryClient.invalidateQueries({ queryKey: ['creators', 'mine'] });
      } else if (accountId === null && items.length > 0) {
        // No accountId on the wire — refresh every tab rather than none.
        queryClient.invalidateQueries({ queryKey: ['inbox'] });
        queryClient.invalidateQueries({ queryKey: ['creators', 'mine'] });
      }
      // Admin all-accounts mode (D-07 FE half): the "All" tab merges every creator's
      // inbox into one list, so ANY inbox:delta (any account) must refresh it — the
      // per-creator invalidation above only targets ['inbox', creatorId, ...], which
      // never prefix-matches ['inbox', 'all', ...].
      if (allAccountsMode) {
        queryClient.invalidateQueries({ queryKey: ['inbox', 'all'] });
        // Admin rollup must refresh on ANY delta too — unconditionally, not only when
        // creatorIdForAccount resolves above. A delta for a real accountId that isn't yet
        // in accountToCreator (e.g. a just-onboarded creator, before ['creators','mine']
        // has refetched) matches NEITHER branch above, so without this the new creator's
        // tab would stay invisible for the whole session (creators/mine staleTime 60s, no
        // refetchInterval). (Codex review HIGH — admin live-monitoring D-07.)
        queryClient.invalidateQueries({ queryKey: ['creators', 'mine'] });
      }
      // If the delta touches the OPEN thread, refetch its messages too — otherwise
      // the new inbound message only re-badges the row and never renders in the pane.
      if (
        selectedConv &&
        (accountId === null || accountId === selectedConv.accountId) &&
        items.some((item) => item._id === selectedConv.maloumChatId)
      ) {
        queryClient.invalidateQueries({ queryKey: ['messages', selectedConv.id] });
      }
    });
    return unsub;
  }, [ws, queryClient, accountToCreator, selectedConv, allAccountsMode]);

  // ---- Auto-redirect to the first creator when none is in the URL ----
  // Admin all-accounts mode (D-04): never redirect — the default landing tab is "All".
  // Phase 13 (D-13-02): defaultToAllTab is the decoupled prop (defaults to !!allAccountsMode,
  // the pre-Phase-13 admin formula) — the chatter all-chats surface passes false so a fresh
  // chatter login still auto-redirects to the first-creator tab.
  React.useEffect(() => {
    if (defaultToAllTab) return;
    if (!creatorIdFromUrl && creators && creators.length > 0) {
      navigate(`${basePath}/${creators[0].creatorId}`, { replace: true });
    }
  }, [creatorIdFromUrl, creators, navigate, defaultToAllTab, basePath]);

  // ---- Selection handlers ----
  const handleSelectCreator = (creatorId: string) => {
    // Preserve the view mode when switching creators so e.g. Transactions stays open.
    const viewParam = activeView !== 'conversations' ? `?view=${activeView}` : '';
    // The 'all' sentinel selects the merged "All" tab. Route it through the explicit `/all`
    // segment (matching handleSelectConversation) — NOT the bare basePath — so
    // creatorIdFromUrl comes back 'all' (still isAllTab) instead of undefined. On the chatter
    // surface defaultToAllTab=false leaves the first-creator auto-redirect effect live, and
    // that effect fires on `!creatorIdFromUrl`; a bare-basePath All tab would be bounced
    // straight back to the first-creator tab (Phase 13 CR-01). 'all' is truthy so the
    // redirect's `!creatorIdFromUrl` guard is false and the All tab sticks; admin is
    // unaffected (defaultToAllTab=true short-circuits the effect).
    navigate(
      creatorId === 'all' ? `${basePath}/all${viewParam}` : `${basePath}/${creatorId}${viewParam}`,
    ); // clears the open thread (belongs to another creator/tab)
    // Reset mark-seen ref so the new creator gets marked on first open.
    lastMarkSeenRef.current = null;
  };

  const handleSelectConversation = (conv: InboxConversationDto) => {
    // On the "All" tab (admin, D-04) activeCreatorId is null. Route through the 'all'
    // sentinel segment so the URL stays valid AND isAllTab survives the next render — the
    // thread then resolves from the merged ['inbox','all'] cache without leaving the All
    // view. (Interpolating a null activeCreatorId here previously produced /chats/null/c/:id,
    // which desynced isAllTab and left the thread unresolvable — Phase 12 CR-02.)
    const seg = isAllTab ? 'all' : activeCreatorId;
    navigate(`${basePath}/${seg}/c/${conv.id}`);
  };

  // ---- Optimistic inbox-cache helpers (per-creator bare array vs "All"-tab InfiniteData) ----
  // The admin "All" tab caches its list as InfiniteData under ['inbox','all',filter] with a null
  // activeCreatorId, whereas a per-creator tab caches a bare InboxConversationDto[] under
  // ['inbox', creatorId, filter]. The write mutations below must patch whichever shape is active
  // or the optimistic update silently no-ops (row badge stays stale until a refetch).
  const activeInboxKey = React.useMemo(
    () => (isAllTab ? (['inbox', 'all'] as const) : (['inbox', activeCreatorId] as const)),
    [isAllTab, activeCreatorId],
  );
  // The creator a conversation belongs to — activeCreatorId on a per-creator tab, or resolved
  // from the row's account on the merged "All" tab (admin), where activeCreatorId is null.
  const creatorIdForConv = React.useCallback(
    (conv: InboxConversationDto) => activeCreatorId ?? accountToCreator[conv.accountId] ?? null,
    [activeCreatorId, accountToCreator],
  );
  // Patch a conversation row in the active inbox cache — both shapes, across every filter-mode
  // variant (setQueriesData prefix-match, like the mute patch in ConversationPane).
  const patchInboxRow = React.useCallback(
    (convId: string, patch: Partial<InboxConversationDto>) => {
      if (isAllTab) {
        queryClient.setQueriesData<InfiniteData<InboxListDto>>(
          { queryKey: ['inbox', 'all'] },
          (old) =>
            old
              ? {
                  ...old,
                  pages: old.pages.map((pg) => ({
                    ...pg,
                    conversations: pg.conversations.map((c) =>
                      c.id === convId ? { ...c, ...patch } : c,
                    ),
                  })),
                }
              : old,
        );
      } else {
        queryClient.setQueriesData<InboxConversationDto[]>(
          { queryKey: ['inbox', activeCreatorId] },
          (old) => old?.map((c) => (c.id === convId ? { ...c, ...patch } : c)),
        );
      }
    },
    [isAllTab, activeCreatorId, queryClient],
  );

  // ---- Mark conversation read (fires when a thread opens) ----
  // Optimistic: clear the row's green bubble + REPLY treatment and subtract this
  // conversation's contribution from the tab-strip count immediately; the server
  // clears unread AND marked_unread on POST /conversations/:id/read (D-10-04).
  // The tab patch mirrors accounts.service's SUM(GREATEST(unread, marked_unread::int)).
  const markReadMut = useMutation({
    mutationFn: (conv: InboxConversationDto) => conversationsApi.markRead(conv.id),
    onMutate: async (conv: InboxConversationDto) => {
      // Cancel in-flight refetches or a response already on the wire would
      // overwrite the optimistic patch with pre-mutation server state.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: activeInboxKey }),
        queryClient.cancelQueries({ queryKey: ['creators', 'mine'] }),
      ]);
      patchInboxRow(conv.id, { unread: 0, isMarkedUnread: false });
      const contribution = Math.max(conv.unread, conv.isMarkedUnread ? 1 : 0);
      const targetCreatorId = creatorIdForConv(conv);
      queryClient.setQueryData<CreatorRollupDto[]>(['creators', 'mine'], (old) =>
        old?.map((creator) =>
          creator.creatorId !== targetCreatorId
            ? creator
            : {
                ...creator,
                tabUnread: Math.max(0, creator.tabUnread - contribution),
                accounts: creator.accounts.map((acc) =>
                  acc.accountId === conv.accountId
                    ? { ...acc, unread: Math.max(0, acc.unread - conv.unread) }
                    : acc,
                ),
              },
        ),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: activeInboxKey });
      queryClient.invalidateQueries({ queryKey: ['creators', 'mine'] });
    },
  });

  // ---- Mark conversation unread (CRM-local, Phase 10, D-10-02/D-10-06) ----
  // Fire-and-forget mutation — does NOT navigate or change the open conversation.
  // Optimistic (the reverse of markReadMut): the badge appears instantly; the tab
  // total only grows when the row contributed nothing yet (GREATEST semantics).
  // onSettled re-syncs both caches — on error that reverts the optimistic patch.
  const markUnreadMut = useMutation({
    mutationFn: (conv: InboxConversationDto) =>
      conversationsApi.markUnread(conv.id, conv.accountId),
    onMutate: async (conv: InboxConversationDto) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: activeInboxKey }),
        queryClient.cancelQueries({ queryKey: ['creators', 'mine'] }),
      ]);
      patchInboxRow(conv.id, { isMarkedUnread: true });
      if (conv.unread === 0) {
        const targetCreatorId = creatorIdForConv(conv);
        queryClient.setQueryData<CreatorRollupDto[]>(['creators', 'mine'], (old) =>
          old?.map((creator) =>
            creator.creatorId === targetCreatorId
              ? { ...creator, tabUnread: creator.tabUnread + 1 }
              : creator,
          ),
        );
      }
    },
    onSuccess: () => {
      addToast('Marked as unread', 'success');
    },
    onError: () => {
      addToast('Failed to mark as unread', 'danger');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: activeInboxKey });
      queryClient.invalidateQueries({ queryKey: ['creators', 'mine'] });
    },
  });

  /**
   * Toggle between 'conversations' and 'transactions' for the active creator.
   * Uses a URL search param (?view=transactions) so it does NOT add a new route
   * that could shadow /inbox/:creatorId/c/:conversationId (Pitfall 7 — RESEARCH.md).
   */
  const handleSelectView = (view: 'conversations' | 'transactions') => {
    // "All" tab (D-13-03): activeCreatorId is null there, so the toggle routes through the
    // explicit `/all` sentinel segment (NOT the bare basePath) — same reason as
    // handleSelectCreator: a bare basePath makes creatorIdFromUrl undefined, which the
    // chatter-live first-creator auto-redirect effect (defaultToAllTab=false) would bounce
    // off the All tab (Phase 13 CR-01). `/all` keeps creatorIdFromUrl==='all' (isAllTab).
    if (isAllTab) {
      navigate(view === 'conversations' ? `${basePath}/all` : `${basePath}/all?view=${view}`);
      return;
    }
    if (!activeCreatorId) return;
    if (view === 'conversations') {
      navigate(`${basePath}/${activeCreatorId}`);
    } else {
      navigate(`${basePath}/${activeCreatorId}?view=${view}`);
    }
  };

  // ---- Loading / error / empty ----
  if (creatorsLoading) {
    return <CenteredMessage>Loading inbox…</CenteredMessage>;
  }
  if (creatorsError) {
    return (
      <CenteredMessage tone="danger">
        Failed to load inbox:{' '}
        {creatorsErrorObj instanceof Error ? creatorsErrorObj.message : 'Unknown error'}
      </CenteredMessage>
    );
  }
  if (!creators || creators.length === 0) {
    // Role-aware copy (Pitfall 5): an admin has no assignments by design, not because
    // there's no data — the assignment-scoped chatter message would be misleading.
    return (
      <CenteredMessage>
        {allAccountsMode ? 'No accounts onboarded yet.' : 'No conversations in your assigned accounts.'}
      </CenteredMessage>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <CreatorTabStrip
        creators={creators}
        activeCreatorId={isAllTab ? 'all' : activeCreatorId}
        healthMap={healthMap}
        onSelect={handleSelectCreator}
        txUnreadMap={txUnreadMap}
        showAllTab={allAccountsMode}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left — conversation queue or transactions list */}
        <div className="w-[340px] shrink-0 border-r border-border overflow-hidden flex flex-col">
          {/* Mass messages (broadcast) — self-service. Opens a panel to view existing campaigns
              + create a new one for the active creator's account(s). Hidden when BroadcastsModal
              is not injected, in read-only mode, or on the admin "All" tab (broadcasts are
              per-creator — pick a creator tab first). */}
          {BroadcastsModal && !readOnly && !isAllTab && (
            <div className="shrink-0 bg-bg-card px-2 pt-2">
              <button
                type="button"
                onClick={() => setBroadcastsOpen(true)}
                disabled={!activeCreator || (activeCreator.accounts?.length ?? 0) === 0}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs font-semibold text-text-muted hover:text-accent hover:border-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
              >
                <span aria-hidden="true">📢</span>
                Mass messages
              </button>
            </div>
          )}
          {/* View toggle: Conversations | Notifications (internal key: 'transactions').
              Hidden when TransactionsPane is not injected or in read-only mode. On the "All"
              tab it renders ONLY when onAllTransactionsViewOpen is supplied (D-13-03) — admin's
              Chats.tsx omits that slot, so its "All" tab stays byte-identical (per-creator
              notifications needs a single creator there); the chatter wrapper supplies it, so
              the merged Notifications view becomes reachable from the chatter All tab. */}
          {TransactionsPane && !readOnly && (!isAllTab || !!onAllTransactionsViewOpen) && (
            <div className="flex items-center gap-0 border-b border-border shrink-0 bg-bg-card px-2 py-1.5">
              <button
                type="button"
                role="tab"
                aria-selected={activeView === 'conversations'}
                onClick={() => handleSelectView('conversations')}
                className={[
                  'flex-1 text-xs font-semibold py-1 px-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                  activeView === 'conversations'
                    ? 'bg-accent/15 text-accent'
                    : 'text-text-muted hover:text-text',
                ].join(' ')}
              >
                Conversations
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeView === 'transactions'}
                onClick={() => handleSelectView('transactions')}
                className={[
                  'flex-1 text-xs font-semibold py-1 px-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                  activeView === 'transactions'
                    ? 'bg-accent/15 text-accent'
                    : 'text-text-muted hover:text-text',
                ].join(' ')}
              >
                {/* Label reads "Notifications" (owner 2026-07-01); the internal
                    view key stays 'transactions' — routing/API/state unchanged. */}
                Notifications
              </button>
            </div>
          )}

          {/* Fallback context notice — only while pagination is NOT yet exhausted
              (allInboxQuery.hasNextPage). It disappears once every page is loaded, and the
              "Load more" affordance at the bottom of the list is the real control. */}
          {isAllTab && allInboxQuery.hasNextPage && (
            <div
              className="border-b border-border px-3 py-1.5 text-[11px] leading-snug text-text-subtle"
              role="status"
            >
              Showing the most recently active conversations across all accounts, newest
              first. Load more at the bottom of the list to page through older ones.
            </div>
          )}

          <div className="flex-1 overflow-hidden">
            {activeView === 'transactions' &&
            TransactionsPane &&
            !readOnly &&
            (!isAllTab || !!onAllTransactionsViewOpen) ? (
              isAllTab ? (
                // Merged Notifications (D-13-03): the assignment-scoped feed across every
                // assigned creator (Plan 02's GET /chatter/transactions/all). creatorId is
                // intentionally '' here — TransactionsPane's allCreators mode derives scope
                // server-side and resolves tap-to-thread from each row's own creatorId.
                <TransactionsPane
                  allCreators
                  creatorId=""
                  creatorName="All accounts"
                  basePath={basePath}
                />
              ) : (
                <TransactionsPane
                  creatorId={activeCreatorId ?? ''}
                  creatorName={activeCreator?.creatorName ?? ''}
                  basePath={basePath}
                />
              )
            ) : (
              <ConversationListPane
                creatorName={isAllTab ? 'All accounts' : (activeCreator?.creatorName ?? '')}
                conversations={conversations}
                refreshing={
                  refreshing || (activeInboxQuery.isPlaceholderData && activeInboxQuery.isFetching)
                }
                isLoading={activeInboxQuery.isLoading}
                isError={activeInboxQuery.isError}
                errorMessage={
                  activeInboxQuery.error instanceof Error ? activeInboxQuery.error.message : undefined
                }
                healthMap={healthMap}
                activeConversationId={selectedConv?.id ?? null}
                onSelect={handleSelectConversation}
                // Phase 10 mark-unread is a chatter workflow write — omitted entirely when
                // readOnly so the hover affordance never renders (D-02).
                onMarkUnread={
                  readOnly ? undefined : (conv: InboxConversationDto) => markUnreadMut.mutate(conv)
                }
                // Explicit list-level mark-READ (clears the unread badge without opening the
                // thread) — the counterpart to onMarkUnread, a deliberate write click gated the
                // same way on !readOnly. Distinct from the ConversationPane's passive auto-mark-
                // read on open (that one stays suppressed for admins via suppressPassiveOnOpen);
                // this gives admins — whose merely opening a thread does NOT clear the badge —
                // an explicit way to remove it. Reuses markReadMut (silent: the badge vanishing
                // is the feedback; a toast here would fire nothing extra over passive-on-open).
                onMarkRead={
                  readOnly ? undefined : (conv: InboxConversationDto) => markReadMut.mutate(conv)
                }
                filterMode={inboxFilter}
                onFilterModeChange={setInboxFilter}
                showCreatorLabel={isAllTab}
                // Admin "All" tab cursor pagination — pull the next server page on demand.
                // Wired only for the merged list; the per-creator path leaves these undefined
                // (chatter is byte-identical — no "Load more" affordance renders).
                hasMore={isAllTab && !!allInboxQuery.hasNextPage}
                isLoadingMore={isAllTab && allInboxQuery.isFetchingNextPage}
                onLoadMore={
                  isAllTab && allInboxQuery.hasNextPage
                    ? () => {
                        void allInboxQuery.fetchNextPage();
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* Centre — active conversation */}
        <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
          {selectedConv ? (
            <ConversationPane
              key={selectedConv.id}
              conv={selectedConv}
              readOnly={readOnly}
              suppressPassiveClaim={suppressPassiveOnOpen}
              // Auto mark-read on open is a real server write (clears the chatter-visible unread
              // badge) — a supervising admin merely glancing at a thread for oversight must never
              // do this, even though they CAN send (T-12-11 spirit). Suppressed with the claim.
              onMarkRead={
                suppressPassiveOnOpen ? undefined : (conv: InboxConversationDto) => markReadMut.mutate(conv)
              }
              onMessageSent={() =>
                queryClient.invalidateQueries({ queryKey: activeInboxKey })
              }
            />
          ) : conversationId && !activeInboxQuery.isPending && !activeInboxQuery.isFetching ? (
            // The URL points at a conversation no loaded list contains and nothing is
            // in flight — e.g. browser Back to a row a server-side filter excludes.
            // Never dead-end on a fake "Loading…" (review finding): name the state and
            // offer the escape hatch.
            <div className="flex flex-col items-center justify-center gap-3 h-full px-6 text-center">
              <span className="text-text-muted text-sm">
                {inboxFilter !== 'all'
                  ? 'This conversation isn’t in the current filter.'
                  : 'This conversation isn’t in the loaded list.'}
              </span>
              {inboxFilter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setInboxFilter('all')}
                  className="text-xs text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
                >
                  Show all conversations
                </button>
              )}
            </div>
          ) : (
            <CenteredMessage>
              {conversationId
                ? 'Loading conversation…'
                : activeView === 'transactions'
                  ? 'Tap a notification to open the fan thread.'
                  : 'Select a conversation from the list to start.'}
            </CenteredMessage>
          )}
        </div>

        {/* Right — fan context (only when a conversation is open) */}
        {selectedConv && (
          <div className="w-[320px] shrink-0 border-l border-border overflow-hidden bg-bg-card">
            <FanPanel
              key={selectedConv.id}
              conversationId={selectedConv.id}
              accountId={selectedConv.accountId}
              memberId={selectedConv.fanMemberId}
              maloumChatId={selectedConv.maloumChatId}
              // Derive the creator from the conversation's account so "Recent payments" shows on
              // the admin "All" tab too (activeCreatorId is null there) — a chatter always has a
              // concrete activeCreatorId so this is unchanged for them (owner 2026-07-20).
              creatorId={creatorIdForConv(selectedConv)}
              fetchRecentPayments={fetchRecentPayments}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>

      {/* Mass messages (broadcast) panel — self-service (chatter + admin full-parity). The
          activeCreator gate keeps it off the admin "All" tab (broadcasts are per-creator). */}
      {BroadcastsModal && activeCreator && !readOnly && (
        <BroadcastsModal
          open={broadcastsOpen}
          onClose={() => setBroadcastsOpen(false)}
          creatorName={activeCreator.creatorName}
          accounts={activeCreator.accounts}
        />
      )}
    </div>
  );
}

function CenteredMessage({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'danger';
}) {
  return (
    <div className="flex items-center justify-center h-full px-6 text-center">
      <span className={tone === 'danger' ? 'text-danger text-sm' : 'text-text-muted text-sm'}>
        {children}
      </span>
    </div>
  );
}
